/* eslint-disable no-continue */

const Logger = require("../Logger");
const Utils = require("../Utils");
const PlaylistItemInfo = require("../Info/PlaylistItemInfo");
const ScreenSharingRenderer = require("../Renderer/ScreenSharingRenderer");
const HTMLTemplateMessaging = require("../Renderer/HTMLTemplateMessaging");
const ViewerDataInfo = require("../Data/ViewerDataInfo");
const ScreenSharingUIController = require("../UI/ScreenSharingUIController");
const PersistentNotificationPanel = require("../UI/PersistentNotificationPanel");
const PersistentNotificationType = require("../Info/PersistentNotificationType");
const ScreenShareMessageType = require("../Info/ScreenShareMessageType");
const ScreenSharingRecoveryHandler = require("./ScreenSharingRecoveryHandler");

const screenSharingTopics = Object.values(ScreenShareMessageType);

const ScreenSharingController = function (item, htmlName, containerName,
  onScheduleReady, onScheduleDone) {
  const factory = {};

  const ScreenSharingMode = {
    STANDARD: "standard",
    MODERATED: "moderated"
  };

  let status;

  let isPlaying = false;
  let isStreaming = false;
  let isModeratorActive = false;
  let joinCode;

  let readyTimer;

  const screenSharingRecovery = ScreenSharingRecoveryHandler({
    htmlName,
    isScreenSharingEnabled: () => ViewerDataInfo.isScreenSharingEnabled(),
    onReload: () => {
      if (isStreaming) {
        isStreaming = false;
      } else {
        joinCode = null;
      }
      ScreenSharingRenderer.reload(htmlName, containerName);
    }
  });

  function isModerated() {
    return ViewerDataInfo.getScreenSharingMode() === ScreenSharingMode.MODERATED;
  }

  const _onPresentationDoneIfPlaying = () => {
    if (isPlaying && onScheduleDone) {
      item.lastStarted = null;

      onScheduleDone();
    }
  };

  function _viewerDataUpdated() {
    ScreenSharingUIController.refresh();
  }

  function _applyModeUI(shouldHidePersistentNotificationPanel = false) {
    PersistentNotificationPanel.setIsModerated(isModerated());

    if (isStreaming) {
      return;
    }

    if (isModerated()) {
      ScreenSharingUIController.reset();
      PersistentNotificationPanel.show(PersistentNotificationType.SCREEN_SHARING_LOBBY);
    } else {
      PersistentNotificationPanel.clear(shouldHidePersistentNotificationPanel);
      if (joinCode) {
        ScreenSharingUIController.setJoinCode(joinCode);
      }
      ScreenSharingUIController.refresh();
    }
  }

  const _messageHandler = (message) => {
    const topic = message.topic;

    if (!screenSharingTopics.includes(topic)) {
      return;
    }

    if (topic === ScreenShareMessageType.HEARTBEAT) {
      screenSharingRecovery.recordHeartbeat();
      return;
    }

    console.log(
      `Viewer received Screen Sharing message ${Object.entries(ScreenShareMessageType).find(t => t[1] === topic)?.[0]}:\n${JSON.stringify(message, null, 2)}`
    );

    if (topic === ScreenShareMessageType.JOIN_MODERATED_SESSION) {
      ViewerDataInfo.setScreenSharingMode(ScreenSharingMode.MODERATED);
      if (message.joinCode) {
        joinCode = message.joinCode;
        PersistentNotificationPanel.setJoinCode(message.joinCode);
      }
      _applyModeUI();
      return;
    }

    if (topic === ScreenShareMessageType.CHANNEL_CONFIG) {
      if (message.allowOthersToJoin !== undefined) {
        PersistentNotificationPanel.setAllowOthersToJoin(message.allowOthersToJoin);
      }
      _applyModeUI();
      return;
    }

    if (topic === ScreenShareMessageType.JOIN_CODE) {
      screenSharingRecovery.onRecovered();
      joinCode = message.joinCode;
      PersistentNotificationPanel.setJoinCode(joinCode);
      ScreenSharingUIController.setJoinCode(joinCode);
      if (!isModerated()) {
        _notifyAllTemplates(message);
      }
      _applyModeUI();
      return;
    }

    if (topic === ScreenShareMessageType.RETURN_TO_STANDARD) {
      ViewerDataInfo.setScreenSharingMode(ScreenSharingMode.STANDARD);
      isModeratorActive = false;
      PersistentNotificationPanel.setIsModeratorActive(isModeratorActive);
      PersistentNotificationPanel.setAllowOthersToJoin(null);
      if (message.joinCode) {
        joinCode = message.joinCode;
      }
      item.isSharing = false;
      _onPresentationDoneIfPlaying();
      _applyModeUI(true);
      return;
    }

    if (topic === ScreenShareMessageType.MODERATOR_ACTIVE && message.isActive !== undefined) {
      const wasModeratorActive = isModeratorActive;
      isModeratorActive = message.isActive;
      PersistentNotificationPanel.setIsModeratorActive(isModeratorActive);

      if (message.isActive && !isPlaying) {
        PersistentNotificationPanel.show(PersistentNotificationType.SCREEN_SHARING_LOBBY);
        _onStreamReady();
      } else if (wasModeratorActive && !message.isActive && !isStreaming) {
        if (isModerated()) {
          PersistentNotificationPanel.show(PersistentNotificationType.SCREEN_SHARING_LOBBY);
        } else {
          PersistentNotificationPanel.clear(true);
          item.isSharing = false;
          _onPresentationDoneIfPlaying();
        }
      }
      return;
    }

    if (topic === ScreenShareMessageType.CHANNEL_ACTIVE) {
      // TODO: Remove this upon Automatic Mode full release
      PersistentNotificationPanel.setIsLegacy(message.isLegacy);
      return;
    }

    if (topic === ScreenShareMessageType.ERROR) {
      ScreenSharingUIController.setConnectionStatus(message.description);
      _notifyAllTemplates(message);
      screenSharingRecovery.onError();
      return;
    }

    if (topic === ScreenShareMessageType.STREAM_CONNECTED) {
      screenSharingRecovery.onRecovered();
      isStreaming = true;
      PersistentNotificationPanel.clear(true);
      ScreenSharingUIController.setScreenSharingPlaying(true);
      _onStreamReady();
      return;
    }

    if (topic === ScreenShareMessageType.STREAM_PAUSED) {
      isStreaming = false;
      PersistentNotificationPanel.show(PersistentNotificationType.SCREEN_SHARING_PAUSED);
      return;
    }

    if (topic === ScreenShareMessageType.STREAM_DISCONNECTED) {
      isStreaming = false;
      ScreenSharingUIController.setScreenSharingPlaying(false);
      if (isModerated()) {
        PersistentNotificationPanel.show(PersistentNotificationType.SCREEN_SHARING_LOBBY);
      } else {
        PersistentNotificationPanel.clear(true);
        item.isSharing = false;
        _onPresentationDoneIfPlaying();
      }
    }
  };

  const htmlTemplateMessaging = new HTMLTemplateMessaging(_onPresentationDoneIfPlaying, htmlName, _messageHandler);

  const _setCurrentItem = () => {
    Logger.setEndpointLoggerContentFields({
      scheduleItemUrl: isPlaying ? "Screen Sharing" : null
    });
  };

  const _notifyAllTemplates = (message) => {
    if (!message || typeof message !== "object") {
      return;
    }

    const messageData = {
      topic: message.topic,
      joinCode: message.joinCode,
      description: message.description
    };
    // save last message for templates that will be loaded later
    ViewerDataInfo.setScreenSharingJoinCodeMessage(messageData);
    // send message to all templates that are already loaded
    htmlTemplateMessaging.postMessageToAllTemplates(messageData);
  };

  factory.init = () => {
    screenSharingRecovery.start();

    ScreenSharingRenderer.render(htmlName, containerName);

    Logger.viewerInfo("Screen sharing schedule initialized", { htmlName });

    ViewerDataInfo.addObserver(_viewerDataUpdated);

    PersistentNotificationPanel.setIsModerated(isModerated());

    if (onScheduleDone) {
      htmlTemplateMessaging.addListenerForTemplatePlayUntilDone();
    }

    htmlTemplateMessaging.onRiseComponentsReady(_onRiseComponentsReady, _onTemplateError);

    // Timer that will cut loading to a maximum of 20 seconds.
    readyTimer = setTimeout(_forceDataReady, 20 * 1000);

    status = PlaylistItemInfo.ADDED_STATUS;

    return 0;
  };

  let _onRiseComponentsReady = () => {
    if (status !== PlaylistItemInfo.READY_STATUS) {
      clearTimeout(readyTimer);

      status = PlaylistItemInfo.READY_STATUS;

      onScheduleReady();
    }
  };

  function _onTemplateError() {
    if (status === PlaylistItemInfo.ADDED_STATUS) {
      clearTimeout(readyTimer);

      status = PlaylistItemInfo.ERROR_STATUS;

      onScheduleReady();
    }
  }

  function _forceDataReady() {
    status = PlaylistItemInfo.READY_STATUS;

    Logger.viewerError("E999999999", "Screen Sharing failed to load in 20 seconds");

    onScheduleReady();
  }

  let _onStreamReady = () => {
    if (!item.isSharing) {
      item.isSharing = true;
      item.lastStarted = new Date();

      onScheduleReady(true);
    }
  };

  factory.unloadSchedule = () => {
    // only unload READY schedules that are NOT playing
    if (status >= PlaylistItemInfo.READY_STATUS && !isPlaying) {
      status = PlaylistItemInfo.UNLOADED_STATUS;

      factory.stop();
    }
  };

  factory.loadSchedule = () => {
    if (status === PlaylistItemInfo.UNLOADED_STATUS) {
      status = PlaylistItemInfo.LOADING_STATUS;
    }
  };

  factory.play = () => {
    if (!isPlaying) {
      isPlaying = true;

      htmlTemplateMessaging.sendPresentationEvent("rise-presentation-play");

      _setVisibility(true);
      _setCurrentItem();
    }
  };

  factory.stop = () => {
    Logger.logDebug(`Screen Sharing - Stop!`);

    item.lastStarted = null;

    // if (isPlaying) {
    isPlaying = false;

    // htmlTemplateMessaging.sendPresentationEvent("rise-presentation-stop");

    _setVisibility(false);
    _setCurrentItem();
    // }
  };

  factory.pause = () => {
    Logger.logDebug(`Screen Sharing - Pause!`);

    item.lastStarted = null;

    // if (isPlaying) {
    isPlaying = false;

    // htmlTemplateMessaging.sendPresentationEvent("rise-presentation-stop");

    _setVisibility(false);
    _setCurrentItem();
    // }
  };

  factory.sendDisplayInterrupted = () => {
    item.lastStarted = null;
    item.isSharing = false;

    PersistentNotificationPanel.clear(true);
    htmlTemplateMessaging.sendPresentationEvent("rise-presentation-stop");
  };

  function _setVisibility(visible) {
    if (visible) {
      window.showElement(htmlName);
    } else {
      window.hideElement(htmlName);
    }
  }

  factory.isReady = () => {
    return status >= PlaylistItemInfo.READY_STATUS;
  };

  factory.getStatus = () => {
    return status;
  };

  factory.checkCanPlay = () => {
    return true;
  }

  factory.unload = () => {
    screenSharingRecovery.stop();
    clearTimeout(readyTimer);

    ViewerDataInfo.removeObserver(_viewerDataUpdated);

    ScreenSharingUIController.reset();
  }

  factory.resize = () => {
    const height = window.innerHeight;
    const width = window.innerWidth;

    // [ad] bug fix - automatically re-size URL iframe size as well
    Utils.resizeContainer("iFrame_" + htmlName, width, height);
  };

  (function () {
    status = PlaylistItemInfo.LOADING_STATUS;
  }());

  return factory;
};

module.exports = ScreenSharingController;
